import {LeanDocument} from 'mongoose';
import {CourseManagerAPI} from '@/classes/api/course-manager.api.class';
import {SubjectManagerApi} from '@/classes/api/subject-manager.api.class';
import {CourseStates} from '@/enums/CourseStates.enum';
import {CourseDocument} from '@/documentTypes/course-manager/Course.document';
import {Employee} from '@/interfaces/employee.interface';
import {PickerItem, PickerOrigin, PickerSlot, PickerTag} from './entityPicker.types';
import {MISSING_CONTRACT_HINT, MISSING_CONTRACT_TAG, teacherContracts} from '@/helpers/contracts/teacherContracts.helper';
import {EmployeeContractStatus} from '@/classes/api/contract-manager.api.class';
import {
    distanceKm,
    formatKm,
    formatSlot,
    idOf,
    isValidOrigin,
    isValidSlot,
    overlapsSlot,
    plural,
    sortByRank,
} from './entityPicker.helper';

export interface TeacherContextOptions {
    /** [lon, lat] of the place the dialog plans for (e.g. the institution) */
    origin?: PickerOrigin | null;
    /** Weekday/time the dialog plans for */
    slot?: PickerSlot | null;
}

type CourseLite = Pick<LeanDocument<CourseDocument>, '_id' | 'customId' | 'dayOfWeek' | 'timeRange' | 'teacher'>;

/** Subject names are static master data – loaded once per session */
const subjectNameCache = new Map<string, string>();

async function loadSubjectNames(ids: string[]): Promise<Map<string, string>> {
    const missing = ids.filter((id) => !subjectNameCache.has(id));
    if (missing.length) {
        try {
            const resp = await SubjectManagerApi.find({
                filter: {_id: missing},
                fields: ['name'],
                skipPagination: true,
            } as any);
            for (const subject of resp.subjects || []) {
                subjectNameCache.set(String(subject._id), subject.name);
            }
        } catch (e) {
            // No read access on subjects: names are simply not shown
        }
    }
    return subjectNameCache;
}

/** One batched query for the active (not ended) courses of all listed teachers */
async function loadActiveCourses(teacherIds: string[]): Promise<CourseLite[] | null> {
    if (!teacherIds.length) {
        return [];
    }
    try {
        const resp = await CourseManagerAPI.find({
            filter: {
                teacher: teacherIds,
                endsAfter: new Date(),
                courseState: [CourseStates.ACTIVE],
            },
            fields: ['customId', 'dayOfWeek', 'timeRange', 'teacher'],
            skipPagination: true,
        } as any);
        return resp.courses as CourseLite[];
    } catch (e) {
        // No read access on courses: the course facts are simply not shown
        return null;
    }
}

/** Distance in km: exact value from a geo query if available, otherwise air-line distance to the origin */
function teacherDistanceKm(employee: Employee, origin: PickerOrigin | null): number | undefined {
    if (typeof employee.distance === 'number' && employee.distance >= 0) {
        return employee.distance / 1000;
    }
    const coords = employee.address?.location?.coordinates;
    if (origin && isValidOrigin(coords)) {
        return distanceKm(origin, coords);
    }
    return undefined;
}

function slotTag(courses: CourseLite[], slot: PickerSlot): PickerTag {
    const conflicts = courses.filter((course) => overlapsSlot(course, slot));
    if (!conflicts.length) {
        return {text: `${formatSlot(slot)} kursfrei`, tone: 'good', icon: 'mdi-calendar-check', title: 'Keine eigenen Kurse zu dieser Zeit'};
    }
    const ids = conflicts.map((course) => course.customId).filter(Boolean).join(', ');
    return {
        text: `${formatSlot(slot)} belegt`,
        tone: 'warn',
        icon: 'mdi-calendar-alert',
        title: ids ? `Überschneidung mit ${ids}` : 'Überschneidung mit einem eigenen Kurs',
    };
}

/** Teacher without employee contract: only a warning, the teacher can still be chosen */
export function missingContractTag(): PickerTag {
    return {text: MISSING_CONTRACT_TAG, tone: 'warn', icon: 'mdi-file-document-alert-outline', title: MISSING_CONTRACT_HINT};
}

function subjectNamesOf(employee: Employee, names: Map<string, string>): string[] {
    return ((employee.subjects || []) as unknown[])
        .map((subject) => {
            if (subject && typeof subject === 'object' && 'name' in (subject as Record<string, unknown>)) {
                return String((subject as { name: string }).name);
            }
            return names.get(idOf(subject)) || '';
        })
        .filter(Boolean);
}

/**
 * Adds distance, subjects, number of active courses and – if a slot is known – whether the teacher
 * has no own course at that time. Ranks nearby teachers first when a location is known.
 * Needs the employee documents in `item.source` (fields: address, subjects, isTeacher).
 */
export async function enrichTeacherItems(items: PickerItem[], options: TeacherContextOptions): Promise<PickerItem[]> {
    const origin = isValidOrigin(options.origin) ? options.origin : null;
    const slot = isValidSlot(options.slot) ? options.slot : null;
    const employees = items.map((item) => item.source as Employee | undefined);
    const teacherIds = employees.filter((e): e is Employee => !!e && e.isTeacher !== false).map((e) => String(e._id));
    const subjectIds = Array.from(new Set(employees.flatMap((e) => ((e?.subjects || []) as unknown[]).map(idOf))));

    const [courses, subjectNames, contracts] = await Promise.all([
        loadActiveCourses(teacherIds),
        loadSubjectNames(subjectIds),
        // one batched, cached request; unknown (no right) = no tag
        teacherContracts.load(teacherIds).catch(() => new Map<string, EmployeeContractStatus>()),
    ]);

    const enriched = items.map((item, index) => {
        const employee = employees[index];
        if (!employee) {
            return item;
        }
        const km = teacherDistanceKm(employee, origin);
        const subjects = subjectNamesOf(employee, subjectNames);
        const context: string[] = [];
        const tags: PickerTag[] = [...(item.tags || [])];
        if (typeof km === 'number') {
            context.push(formatKm(km));
        }
        const isTeacher = employee.isTeacher !== false;
        if (courses && isTeacher) {
            const own = courses.filter((course) => idOf(course.teacher) === String(employee._id));
            context.push(own.length ? plural(own.length, 'aktiver Kurs', 'aktive Kurse') : 'keine aktiven Kurse');
            if (slot) {
                tags.push(slotTag(own, slot));
            }
        }
        if (isTeacher && contracts.get(String(employee._id))?.hasContract === false) {
            tags.push(missingContractTag());
        }
        const city = employee.address?.city;
        const subtitle = [city, subjects.length ? subjects.join(', ') : ''].filter(Boolean).join(' · ');
        return {
            ...item,
            tags,
            context: [...context, ...(item.context || [])],
            subtitle: subtitle || item.subtitle,
            rank: km,
        };
    });
    return origin ? sortByRank(enriched) : enriched;
}
